import { getGoogleApiValidation } from 'lib/googleApis/GoogleAdressValidation'
import { isNilOrEmpty,isNotNilOrEmpty } from 'shared/utils/ramda'

import {
  AddressValidationResponse,
  CustomerAddress,
  CustomerInvoiceAddress,
  InvoiceType
} from './types'


export const findDefaultInvoiceAddress = (
  addresses: CustomerInvoiceAddress[]
) => {
  return addresses.find(el => el.isDefault)?.id || addresses?.[0]?.id || ''
}

export const isAddressExistInAddresses = (
  addresses: CustomerInvoiceAddress[],
  addressId: string
) => {
  if (!addressId) return false

  return addresses.some(({ id }) => id === addressId)
}

interface ValidateCustomerAddressProps {
  postalCode: string
  city: string
  street: string
  houseNumber: string
}

interface ValidationResultAddressComponent {
  given: string
  recommended: string
}

export interface ValidateCustomerAddressResult {
  isAnyUnconfirmed: boolean
  hasAnySlot: boolean
  unconfirmed: {
    [key: string]: ValidationResultAddressComponent
  } | null
  hasDifferences: boolean
  differences: {
    [key: string]: ValidationResultAddressComponent
  } | null
  values: {
    [key: string]: ValidationResultAddressComponent
  } | null
}

export const getAddressCoordinates = async (
  postalCode: string,
  city: string,
  street: string,
  houseNumber: string
) => {
  const resp = await getGoogleApiValidation(
    postalCode,
    city,
    street,
    houseNumber
  )

  if ([200, 201].includes(resp.status)) {
    const response = resp.data as AddressValidationResponse

    return response.result.geocode.location
  }

  return null
}

// `checkDeliveryCoverage` comes from DeliveryAddressPort (customer must not
// depend on delivery).
export const validateCustomerAddress = async (
  { postalCode, city, street, houseNumber }: ValidateCustomerAddressProps,
  checkDeliveryCoverage: (postalCode: string, city: string) => Promise<boolean>
) => {
  try {
    const resp = await getGoogleApiValidation(
      postalCode,
      city,
      street,
      houseNumber
    )

    if ([200, 201].includes(resp.status)) {
      const response = resp.data as AddressValidationResponse

      const addressComponents = response.result.address.addressComponents

      const hasAnySlot = await checkDeliveryCoverage(postalCode, city)

      const values = {}
      const differences = {}
      const unconfirmed = {}

      const subpremise =
        addressComponents.find(c => c.componentType === 'subpremise') || null
      const hasSupremise = isNotNilOrEmpty(subpremise)
      const subpremiseText = subpremise?.componentName?.text || null
      const subpremiseConfirmationLevel = subpremise?.confirmationLevel || ''
      const subpremiseConfirmed =
        subpremiseConfirmationLevel === 'CONFIRMED' ||
        subpremiseConfirmationLevel?.includes('PLAUSIBLE')

      const streetNumber =
        addressComponents.find(c => c.componentType === 'street_number')
          ?.componentName?.text || ''
      const houseNum = subpremiseText
        ? `${streetNumber}/${subpremiseText}`
        : streetNumber

      addressComponents.forEach(component => {
        const { componentType, confirmationLevel, componentName } = component
        const text = componentName?.text || ''

        const value = text.toLowerCase()

        if (componentType === 'route') {
          values['street'] = {
            given: street,
            recommended: text
          }

          if (value !== street.toLowerCase()) {
            differences['street'] = {
              given: street,
              recommended: text
            }
          }

          if (confirmationLevel.includes('UNCONFIRMED_AND_SUSPICIOUS')) {
            unconfirmed['street'] = {
              given: street,
              recommended: text
            }
          }
        } else if (componentType === 'street_number') {
          values['houseNumber'] = {
            given: houseNumber,
            recommended: houseNum
          }

          if (houseNum !== houseNumber.toLowerCase()) {
            differences['houseNumber'] = {
              given: houseNumber,
              recommended: houseNum
            }
          }

          if (confirmationLevel === 'UNCONFIRMED') {
            unconfirmed['houseNumber'] = {
              given: houseNumber,
              recommended: houseNum
            }
          }
        } else if (componentType === 'locality') {
          if (!values['city']) {
            values['city'] = {
              given: city,
              recommended: text
            }
            if (value !== city.toLowerCase()) {
              differences['city'] = {
                given: city,
                recommended: text
              }
            }

            if (confirmationLevel.includes('UNCONFIRMED')) {
              unconfirmed['city'] = {
                given: city,
                recommended: text
              }
            }
          }
        } else if (componentType === 'postal_code') {
          values['postalCode'] = {
            given: postalCode,
            recommended: text
          }
          if (value !== postalCode.toLowerCase()) {
            differences['postalCode'] = {
              given: postalCode,
              recommended: text
            }
          }

          if (confirmationLevel.includes('UNCONFIRMED')) {
            unconfirmed['postalCode'] = {
              given: postalCode,
              recommended: text
            }
          }
        } else if (componentType === 'premise') {
          values['houseNumber'] = {
            given: houseNumber,
            recommended: text
          }
          if (value !== houseNumber.toLowerCase()) {
            differences['houseNumber'] = {
              given: houseNumber,
              recommended: text
            }
          }

          if (confirmationLevel.includes('UNCONFIRMED')) {
            unconfirmed['houseNumber'] = {
              given: houseNumber,
              recommended: text
            }
          }
        }
      })

      // @ts-ignore
      if (isNilOrEmpty(values.houseNumber)) {
        values['houseNumber'] = {
          given: houseNumber,
          recommended: houseNumber.replace(/\/.*/, '')
        }

        differences['houseNumber'] = {
          given: houseNumber,
          recommended: houseNumber.replace(/\/.*/, '')
        }

        unconfirmed['houseNumber'] = {
          given: houseNumber,
          recommended: houseNumber.replace(/\/.*/, '')
        }
      }

      if (hasSupremise && !subpremiseConfirmed) {
        unconfirmed['subpremise'] = {
          given: subpremiseText
        }
      }

      const isAnyUnconfirmed = isNotNilOrEmpty(unconfirmed)
      const hasDifferences = isNotNilOrEmpty(differences)

      return {
        isAnyUnconfirmed,
        unconfirmed: isAnyUnconfirmed ? unconfirmed : null,
        hasDifferences,
        differences: hasDifferences ? differences : null,
        coordinates: response.result.geocode.location,
        values,
        hasAnySlot
      }
    }

    return null
  } catch (err) {
    console.error(err)

    return null
  }
}

export const isCompanyInvoiceAddress = (
  address: CustomerAddress | CustomerInvoiceAddress
): boolean =>
  address.type === 'invoice' &&
  'invoiceType' in address &&
  address.invoiceType === InvoiceType.company

export const IS_PERSONAL_INVOICE_ENABLED = false
